import { uniq as _uniq }               from 'lodash';
import { observable, observableArray, computed } from 'knockout';

/**
 * Data una string di tipo "data-bind", la converte in un oggetto valido.
 *
 * @param {string} string
 * @return {Object}
 * @private
 */
export function _parseDataBind(string = '') {
  const finalObj = {};
  
  if (!string) {
    return finalObj;
  }
  
  if (typeof string !== 'string') {
    console.warn('Valore non di tipo stringa per la function "_parseDataBind()"');
    
    return finalObj;
  }
  
  if (string.indexOf('function') > -1) {
    console.warn('Al momento non è possibile inserire delle funzioni (function(){}) nel data-bind.');
  }
  
  /**
   * Stringa su cui testare la funzione:
   * visible: txtTipo() === 'I' || txtTipo() >= 'M' && presentata() === 'si' && test2() <= 'iuaas', attr: {readonly: ritiro_presso_ubicazione() > 0 && test() === 'assad'}, enable: txtTipo() === 'M' && txtTipo() !== 'A', text: price() > 50 ? `expensive` : `cheap`, enable: parseAreaCode(cellphoneNumber()) != '555',with: {emotion: 'happy', 'facial-expression': 'smile'}
   *
   * oldRegexp: ([a-zA-Z0-9_()!=\s'"\/]+[!&=]+[a-zA-Z0-9_()!=\s'"|\/]+)|([a-zA-Z0-9_()!='"|]+)
   * eslint-disable-next-line no-useless-escape
   */
  const parsedString = string.trim().replace(/([\s'"]+|^|)([a-zA-Z_-]+)(['"]|):|(.*?)((?=[,{}])|$)/g,
    function(a) {
      let isObjKey = false;
      let string   = a.trim();
      
      if (!string) {
        return '';
      }
      
      // Se ci sono : indica che è la chiave di un oggetto.
      // Rimuove quindi gli apici singoli qualora ci siano
      if (string.lastIndexOf(':') === (string.length - 1)) {
        string = string.slice(0, string.lastIndexOf(':'));
        
        string = string.replace(/'/g, '');
        
        isObjKey = true;
      }
      
      // Esegue l'escape degli apici " in quanto questi sono usati dal json.
      string = string.replace(/"/g, `\\"`);
      string = `"${string}"`;
      
      // Se la stringa non finisce e inizia con apici, ci aggiunge gli apici doppi.
      // Commentato perchè sembra che se si voleva avere un valore tipo stringa, togliendo gli apici diventa variabile e ritorna errori.
      /*
       if (!string.match(/^'(.*?)'$/)) {
       string = `"${string}"`;
       } else { // Se la stringa inizia e finisce con apici singoli li sostituisce con quelli doppi
       string = `"${string.slice(1, string.length - 1)}"`;
       }
       */
      
      // Se la stringa è la chiave di un oggetto, alla fine della stringa aggiunge i :
      if (isObjKey) {
        string += ':';
      }
      
      return string;
    });
  
  return JSON.parse(`{${parsedString}}`);
}

/**
 * Dato un oggetto, lo converte in una stringa valida da passare come attributo "data-bind"
 * leggibile poi dalla libreria knockout.js
 *
 * @param {Object} objData
 * @return {string}
 * @private
 */
export function _stringifyDataBind (objData, cleanSpaces) {
  const finalData = [];
  
  Object.keys(objData).forEach(key => {
    finalData.push(`${key}: ${JSON.stringify(objData[key]).replace(/"/g, '')}`);
  });
  
  let data = finalData.join(', ');
  
  // Rimuove gli spazi superiori a 2 e gli a capo
  if (cleanSpaces) {
    return data.replace(/\\n\s{2,}/g, '');
  }
  
  return data;
}

/**
 * Aggiunge all'attributo "data-bind" altri dati passati come oggetto dentro il parametro newData.
 *
 * Nel caso la proprietà esista già, verrà effettuato un merge delle proprietà, ed in alcuni casi questa
 * potrebbe essere convertita in una computed property.
 *
 * @param {HTMLElement} element
 * @param {string} element.originalDataBind
 *
 * @param {string} newData
 * @param {Boolean} override
 * @private
 */
export function _appendDataBindAttr (element, newData, override = false, viewModel) {
  const currentDataBind = element.getAttribute('data-bind');
  const currentData     = _parseDataBind(currentDataBind);
  const finalData       = {};
  let keys              = Object.keys(currentData);
  
  // Nuovi dati da aggiungere
  newData = _parseDataBind(newData);
  
  // Esegue un merge delle chiavi già presenti e quelle nuove da aggiungere.
  keys = keys.concat(Object.keys(newData));
  
  // Dopo aver eliminato le chiavi doppie, esegue un foreach per ciascuna.
  _uniq(keys).forEach(key => {
    // Se la chiave non esiste ancora la definisce ma la lascia vuota.
    if (!finalData[key]) {
      finalData[key] = undefined;
    }
    
    // Se attualmente la chiave esiste sull'oggetto, la copia sul nuovo oggetto finale.
    if (currentData[key]) {
      finalData[key] = currentData[key];
    }
    
    // Se i nuovi dati contengono la stessa classe e se questa è diversa da quella eventualmente già presente,
    // la aggiunge o esegue il merge.
    if (newData[key] && newData[key] !== finalData[key]) {
      const newVal = newData[key];
      
      // Se la chiave esiste già nell'oggetto finale e se la chiave è checked,
      // crea una computedProperty.
      // Questo avviene solo per le checkbox.
      if (finalData[key] && key === 'checked') {
        const originalKey      = finalData[key];
        const parenthesesIndex = finalData[key].indexOf('(');
        
        const computedKey = finalData[key].slice(0, parenthesesIndex > 0 ? parenthesesIndex : undefined).replace(/!/g, '') + '_computed';
        
        // Se c'è un secondo valore per una chiave già esistente, crea una computed property...
        viewModel[computedKey] = computed({
          read:  function () {
            function evaluate (str) {
              str = str.replace(/!/g, '!this.');
              
              if (str.indexOf('!') === -1) {
                str = 'this.' + str;
              }
              
              // eslint-disable-next-line no-eval
              return eval(str);
            }
            
            const secondary = evaluate.call(this.model, this.newVal);
            
            this.model[originalKey](secondary);
            
            return secondary;
          },
          write: function (value) {
            this.model[originalKey](value);
          },
          owner: {
            element: element,
            newVal:  newVal,
            model:   viewModel
          }
        });
        
        // Crea un evento relativo alla computedProperty sul change della checkbox
        // in modo che nel deselect di quest'ultima venga correttamente salvato il valore false,
        // che altrimenti verrebbe ignorato dal metodo write della computedProperty probabilmente perchè "false".
        viewModel[computedKey + '_onChange'] = function (a, e) {
          if (e.target.checked === false) {
            this[e.target.name](false);
          }
        };
        
        finalData[key] = `${computedKey}, event:{ change: ${computedKey + '_onChange'}}`;
        
        return;
      }
      
      // Se il nuovo valore è di tipo string, esegue il merge con quella già esistente,
      // altrimenti la sostituisce in toto.
      if (typeof newVal === 'string') {
        // Se il valore già esistente contiene un metodo, lo aggiunge come primo elemento.
        if (finalData[key] && finalData[key].indexOf('(') > -1) {
          finalData[key] = finalData[key] ? `${newVal} && ${finalData[key]}` : newVal;
          
          return;
        }
        
        finalData[key] = finalData[key] ? `${finalData[key]} && ${newVal}` : newVal;
      } else {
        finalData[key] = newData[key];
      }
    }
  });
  
  // Scrive l'attributo "data-bind" se questo non esiste ancora
  if (!element.originalDataBind) {
    element.originalDataBind = element.getAttribute('data-bind');
  }
  
  element.setAttribute('data-bind', _stringifyDataBind(finalData));
}

export function _makeObservable(obj) {
  if (typeof obj === "number" || typeof obj === "string"){
    return observable(obj);
  } else if (obj instanceof Array){
    return observableArray(obj);
  }
  
  const keys   = Object.keys(obj);
  const newObj = {};
  
  Object.entries(obj).forEach(([key, value]) => {
    if (['string', 'number', 'boolean'].includes(typeof value) || value === null || value === undefined) {
      value = observable(value);
    } else if (value instanceof Array) {
      value = observableArray(value);
    } else if (typeof value === 'function') {
      value = value;
    } else {
      // Cerco di capire se il valore è un altro oggetto.. se si rendo observale anche i valori interni.
      try {
        if (Object.keys(value)) {
          value = _makeObservable(value);
        }
      } catch (e) {
        console.log(e);
      }
    }
  
    newObj[key] = value;
  });
  
  return newObj;
}

export function _mergeData(oldData, newData) {
  const keys = Object.keys(oldData);
  
  for (const key of keys) {
    if (typeof oldData[key] === 'function') {
      const newValue = newData[key] !== undefined ? newData[key] : null;
      
      oldData[key](newValue);
    } else {
      _mergeData(oldData[key], newData[key]);
    }
  }
}
